// Hooks de datos del handoff v3. Paridad: mismas fuentes que el dashboard
// (RTDB /signals_history vía useClientStats, /radar_vip_results vía
// useRadarResults, /today_games, Firestore users/{uid}). Nada se calcula
// en paralelo: los helpers de día Chile vienen de PerformanceLab.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { doc, updateDoc } from 'firebase/firestore';
import { db, firestore } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import { santiagoDateStr, santiagoMidnightToUTCms, shiftDays, statsOfSubset } from '../../components/PerformanceLab';
import useClientStats from '../hooks/useClientStats';
import useRadarResults from '../../hooks/useRadarResults';
import { matchOf, pickOf } from '../components/SignalCard';

// Ventana de emisión legible: signals_history trae `ventana_label` (texto),
// `ventana` (a veces booleano) y `telegram_window`.
export function ventanaDe(s) {
  // handball: mitad + minuto de emisión ("2T · 35'"), mismo formato que
  // las cards en vivo; el label largo ("2ª mitad temprana") rompe columnas.
  const esHb = s.sport === 'handball' || s.match_type === 'handball' || /^[12]H/.test(String(s.ventana || ''));
  if (esHb && s.minuto != null && !Number.isNaN(Number(s.minuto))) {
    return `${String(s.ventana || '').startsWith('1H') || Number(s.minuto) <= 30 ? '1T' : '2T'} · ${Math.round(Number(s.minuto))}'`;
  }
  const cand = [s.ventana_label, s.ventana].find((v) => typeof v === 'string' && v.trim());
  if (cand) return cand.trim();
  // basquet: `period` numerico (+ reloj "MM:SS" si viene)
  const per = Number(s.period ?? s.period_emitido ?? s.periodo);
  if (per >= 1 && per <= 4) {
    const clk = typeof s.clock === 'string' && s.clock.includes(':') ? ` · ${s.clock}` : '';
    return `Q${per}${clk}`;
  }
  return 'vivo';
}

// ── red ──────────────────────────────────────────────────────────────
export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  useEffect(() => {
    const on = () => { setOnline(true); };
    const off = () => { setOnline(false); setLastSync((v) => v || new Date()); };
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  const sinceStr = lastSync
    ? new Intl.DateTimeFormat('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Santiago' }).format(lastSync)
    : null;
  return { online, sinceStr };
}

// ── preferencias de alertas (users/{uid}.alertPrefs) ─────────────────
// Campo de perfil: las rules permiten al dueño escribirlo. El bot todavía
// no lo lee (ver reporte FASE 1): hoy documenta la preferencia del cliente.
export const ALERT_DEFAULTS = { entrar: true, vigilar: false, resultados: true, resumen: true,
  tgEntrar: true, tgVigilar: false, tgResultados: true, tgResumen: false };
export function useAlertPrefs() {
  const { user, userData } = useAuth();
  const prefs = useMemo(() => ({ ...ALERT_DEFAULTS, ...(userData?.alertPrefs || {}) }), [userData]);
  const set = useCallback(async (key, value) => {
    if (!user) return;
    try {
      await updateDoc(doc(firestore, 'users', user.uid), { alertPrefs: { ...prefs, [key]: value } });
    } catch (e) { console.warn('[alertPrefs]', e); }
  }, [user, prefs]);
  return { prefs, set };
}

// ── agenda: /today_games/{YYYY-MM-DD} (la escribe prematch_list.py) ────
export function useTodayGames(dateStr) {
  const [raw, setRaw] = useState(undefined);   // undefined = cargando
  useEffect(() => {
    if (!dateStr) return undefined;
    setRaw(undefined);
    const unsub = onValue(ref(db, `today_games/${dateStr}`), (s) => setRaw(s.val() || null), () => setRaw(null));
    return () => unsub();
  }, [dateStr]);
  const games = useMemo(() => {
    if (!raw) return [];
    return Object.entries(raw).map(([gid, g]) => ({
      gid, ...g,
      cobertura: !g.is_bl && g.categoria !== 'no_recomendada',
      esHandball: g.match_type === 'handball' || /handball|hbl|ehf|asobal|starligue|handbold/i.test(g.liga || ''),
      horaNum: parseInt(String(g.hora || '').replace(':', ''), 10) || 0,
    })).sort((a, b) => a.horaNum - b.horaNum);
  }, [raw]);
  return { games, loading: raw === undefined, vacio: raw === null };
}

// Recordatorios de agenda (campana): localStorage + Notification API
// mientras la app esté abierta. Sin backend de push programado (ver reporte).
const REM_KEY = 'ee-app-reminders';
export function useReminders() {
  const read = () => { try { return JSON.parse(localStorage.getItem(REM_KEY) || '{}'); } catch (_e) { return {}; } };
  const [map, setMap] = useState(read);
  const toggle = useCallback((gid, meta) => {
    setMap((m) => {
      const n = { ...m };
      if (n[gid]) delete n[gid]; else n[gid] = { ...meta, at: Date.now() };
      try { localStorage.setItem(REM_KEY, JSON.stringify(n)); } catch (_e) { /* noop */ }
      return n;
    });
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);
  return { reminders: map, toggle };
}

// ── notificaciones: derivadas de señales (ENTRAR + resultado), RADAR y
//    cuenta. Estado leído/no leído en localStorage. ──────────────────
const READ_KEY = 'ee-app-notif-read';
function readSet() { try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]')); } catch (_e) { return new Set(); } }

export function useNotificaciones() {
  const { userData } = useAuth();
  const { signals, loading: l1 } = useClientStats();
  const { alerts, loading: l2 } = useRadarResults();
  const [read, setRead] = useState(readSet);

  const items = useMemo(() => {
    const out = [];
    const now = Date.now();
    const hace7 = now - 7 * 864e5;
    for (const s of signals) {
      const t0 = s.started_at || s.created_at || 0;
      if (t0 < hace7) continue;
      const liga = s.league || s.liga || '—';
      const edge = parseFloat(s.edge_initial ?? s.edge);
      const edgeTxt = Number.isNaN(edge) ? '' : `${edge > 0 ? '+' : '−'}${Math.abs(edge).toFixed(1)}`;
      out.push({
        id: `sig_${s.id}`, ts: t0, kind: 'señal', icon: 'g', gid: s.game_id,
        k: `Entrar · ${liga}`, tt: `${pickOf(s)} · ${matchOf(s)}`,
        dd: [`Emitida en ${ventanaDe(s)}`, edgeTxt && `Edge <b>${edgeTxt}</b>`].filter(Boolean).join(' · '),
      });
      if (s.result === 'WIN' || s.result === 'LOSS') {
        const tr = s.ended_at || s.resolved_at || t0 + 1;
        out.push({
          id: `res_${s.id}`, ts: typeof tr === 'number' ? tr : Date.parse(tr) || t0 + 1, kind: 'resultado', gid: s.game_id,
          icon: s.result === 'WIN' ? 'g' : 'n', k: `Resultado · ${s.result === 'WIN' ? 'Win' : 'Loss'}`,
          tt: matchOf(s), dd: `${pickOf(s)} cerró <b>${s.result}</b>.`,
        });
      }
    }
    for (const a of alerts) {
      if (a.ts < hace7) continue;
      const edgeTxt = a.edge == null ? '' : `${a.edge > 0 ? '+' : '−'}${Math.abs(a.edge).toFixed(1)}`;
      out.push({
        id: `radar_${a.id}`, ts: a.ts, kind: 'señal', icon: 'w', gid: a.gid,
        k: `Radar · ${a.liga}`, tt: `${a.side} ${a.linea ?? ''} · ${a.partido.replace(' @ ', ' vs ')}`,
        dd: [`Ventana ${a.ventana}`, edgeTxt && `Edge <b>${edgeTxt}</b>`].filter(Boolean).join(' · '),
      });
      if (a.resolved) {
        out.push({
          id: `radarres_${a.id}`, ts: a.ts + 1, kind: 'resultado', gid: a.gid,
          icon: a.won ? 'g' : 'n', k: `Resultado · ${a.won ? 'Win' : 'Loss'} · Radar`,
          tt: a.partido.replace(' @ ', ' vs '), dd: `${a.side} ${a.linea ?? ''} cerró <b>${a.won ? 'WIN' : 'LOSS'}</b>${a.total_final != null ? ` · Total ${a.total_final}` : ''}.`,
        });
      }
    }
    // Resumen diario de ayer (misma fuente y mismos helpers que Performance Lab)
    const ayer = shiftDays(santiagoDateStr(new Date()), -1);
    const a0 = santiagoMidnightToUTCms(ayer), a1 = santiagoMidnightToUTCms(shiftDays(ayer, 1));
    const subset = signals.filter((s) => (s.started_at || 0) >= a0 && (s.started_at || 0) < a1);
    const st = statsOfSubset(subset);
    if (st.decided > 0) {
      const dia = new Intl.DateTimeFormat('es-CL', { weekday: 'long', timeZone: 'America/Santiago' }).format(new Date(a0 + 12 * 36e5));
      out.push({
        id: `resumen_${ayer}`, ts: a1, kind: 'cuenta', icon: '',
        k: 'Resumen diario', tt: `${dia.charAt(0).toUpperCase() + dia.slice(1)}: ${st.wins} de ${st.decided} aciertos`,
        dd: `Acierto del día <b>${st.winRate != null ? st.winRate.toFixed(0) : 0}%</b>.`,
      });
    }
    if (userData?.telegramChatId) {
      const t = userData.telegramLinkedAt?.seconds ? userData.telegramLinkedAt.seconds * 1000 : null;
      out.push({
        id: 'cuenta_tg', ts: t || hace7, kind: 'cuenta', icon: 'd',
        k: 'Cuenta', tt: 'Telegram vinculado',
        dd: `Recibirás las señales también en ${userData.telegramUsername ? '@' + userData.telegramUsername : 'Telegram'}.`,
      });
    }
    out.sort((a, b) => b.ts - a.ts);
    return out.map((n) => ({ ...n, unread: !read.has(n.id) }));
  }, [signals, alerts, userData, read]);

  const markAllRead = useCallback(() => {
    const s = new Set(items.map((n) => n.id));
    try { localStorage.setItem(READ_KEY, JSON.stringify([...s])); } catch (_e) { /* noop */ }
    setRead(s);
  }, [items]);
  const markRead = useCallback((id) => {
    setRead((r) => { const n = new Set(r); n.add(id); try { localStorage.setItem(READ_KEY, JSON.stringify([...n])); } catch (_e) { /* noop */ } return n; });
  }, []);

  return { items, loading: l1 || l2, unread: items.filter((n) => n.unread).length, markAllRead, markRead };
}

// ── hora relativa ("hace 4 min", "11:20", "ayer") ─────────────────────
export function relTime(ts) {
  const d = Date.now() - ts;
  if (d < 60e3) return 'ahora';
  if (d < 3600e3) return `hace ${Math.floor(d / 60e3)} min`;
  const hoy = santiagoDateStr(new Date()), ese = santiagoDateStr(new Date(ts));
  if (ese === hoy) return new Intl.DateTimeFormat('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Santiago' }).format(new Date(ts));
  if (ese === shiftDays(hoy, -1)) return 'ayer';
  return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', timeZone: 'America/Santiago' }).format(new Date(ts));
}
export function dayGroup(ts) {
  const hoy = santiagoDateStr(new Date()), ese = santiagoDateStr(new Date(ts));
  if (ese === hoy) return 'Hoy';
  if (ese === shiftDays(hoy, -1)) return 'Ayer';
  return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'long', timeZone: 'America/Santiago' }).format(new Date(ts));
}
